import { trpc } from "@util/trpc/trpcClient";
import { useCallback, useMemo } from "react";
import { useLocalStorage, useReadLocalStorage } from "usehooks-ts";

type LocalStorageValue = number[];
const LOCAL_STORAGE_KEY = "readNotifIds";

// Set a limit on the number we store locally just so there's some bound
// on the set operations
const STORAGE_LIMIT = 500;

/**
 * Checks if any unread notifications have not been "seen" by the user. This
 * state is different from read/dismissed in that its only stored by the browser.
 */
export function useHasUnseenNotifs(): boolean {
  const { data } = trpc.inAppNotifications.getInAppNotifications.useQuery(
    undefined,
    {
      refetchInterval: 5 * 60 * 1_000,
    },
  );

  const notifs = data?.inAppNotifications || [];
  const unreadNotifIds = notifs.filter((n) => !n.read).map((n) => n.id);
  const seenNotifIds =
    useReadLocalStorage<LocalStorageValue>(LOCAL_STORAGE_KEY);

  return useMemo(
    // Check if any unread notifications have not been seen
    () => unreadNotifIds.some((id) => !(seenNotifIds || []).includes(id)),
    // useLocalStorage memoizes values so this is a safe dependency
    [unreadNotifIds, seenNotifIds],
  );
}

/**
 * Marks all loaded unread notifications as "seen" locally, which will dismiss
 * the indicator on the notification button.
 */
export function useMarkAllNotifsAsSeen(): () => void {
  const { data } = trpc.inAppNotifications.getInAppNotifications.useQuery(
    undefined,
    {
      // Rely on useHasUnseenNotifs to supply data
      enabled: false,
    },
  );

  const unreadNotifIds = useMemo(
    () =>
      (data?.inAppNotifications || []).filter((n) => !n.read).map((n) => n.id),
    [data?.inAppNotifications],
  );

  const markNotifsAsSeen = useMarkNotifsAsSeen();

  return useCallback(() => {
    if (unreadNotifIds.length > 0) {
      markNotifsAsSeen(unreadNotifIds);
    }
  }, [unreadNotifIds, markNotifsAsSeen]);
}

/**
 * Returns a function that will mark specific notifications as "seen" locally, which
 * will dismiss the indicator on the notification button.
 */
export function useMarkNotifsAsSeen(): (notifIds: number[]) => void {
  const [, setSeenNotifIds] = useLocalStorage<LocalStorageValue>(
    LOCAL_STORAGE_KEY,
    [],
  );

  const markAsSeen = useCallback(
    (notifIds: number[]) => {
      setSeenNotifIds((prev) => {
        const newSeenNotifIds = Array.from(
          new Set([...prev, ...notifIds]),
        ).slice(-STORAGE_LIMIT);

        return newSeenNotifIds;
      });
    },
    [setSeenNotifIds],
  );

  return markAsSeen;
}
